// src/utils/luxVariables.js
// -----------------------------------------------------------------------------
// Utilitaires pour la gestion des variables LUX (Voiceflow)
// -----------------------------------------------------------------------------
import { vfUpdateVariables } from '../api/VoiceflowInteractions';

/**
 * Extrait les métiers explorés et pistes sérieuses depuis l'historique
 * @param {Array} history - L'historique des actions
 * @param {Object} itemsPro - Les interactions pro avec les titres de métiers
 * @returns {Object} { metiersExplores: string, pistesSerieuses: string }
 */
export function getMetiersFromHistory(history, itemsPro) {
  if (!history || !Array.isArray(history)) {
    return { metiersExplores: '', pistesSerieuses: '' };
  }

  // Types d'actions qui indiquent une exploration de métier
  const explorationTypes = [
    'JOB_PAGE',
    'QUIZZ_STARTED',
    'QUIZZ_ENDED',
    'GRIT_AND_FIT_STARTED',
    'GRIT_AND_FIT_SCENARIO_STARTED',
    'GRIT_AND_FIT_SCENARIO_ENDED',
    'EXPERT_CHAT_STARTED'
  ];

  // Types d'actions qui indiquent un engagement sérieux
  const seriousTypes = [
    'QUIZZ_ENDED',
    'GRIT_AND_FIT_SCENARIO_ENDED',
    'EXPERT_CHAT_STARTED'
  ];

  const metiersExploresSet = new Set();
  const pistesSerieusesSet = new Set();

  history.forEach(item => {
    if (!item.ciaov2_job_id) return;

    // Récupérer le titre du métier depuis itemsPro ou parser le content
    let metierTitle = null;

    if (itemsPro && itemsPro[item.ciaov2_job_id]) {
      metierTitle = itemsPro[item.ciaov2_job_id].title;
    } else {
      // Fallback: essayer d'extraire le titre depuis le content
      metierTitle = extractMetierFromContent(item.content);
    }

    if (!metierTitle) return;

    // Ajouter aux métiers explorés
    if (explorationTypes.includes(item.type)) {
      metiersExploresSet.add(metierTitle);
    }

    // Ajouter aux pistes sérieuses si engagement fort
    if (seriousTypes.includes(item.type)) {
      pistesSerieusesSet.add(metierTitle);
    }
  });

  return {
    metiersExplores: Array.from(metiersExploresSet).join(', '),
    pistesSerieuses: Array.from(pistesSerieusesSet).join(', ')
  };
}

/**
 * Extrait le titre du métier depuis le contenu textuel de l'historique
 * @param {string} content - Le contenu de l'action
 * @returns {string|null} Le titre du métier ou null
 */
function extractMetierFromContent(content) {
  if (!content) return null;

  // Patterns pour extraire le métier
  const patterns = [
    /métier de ([^.]+)\./,
    /fiche du métier de ([^.]+)\./,
    /quizz pour le métier de ([^.]+)/,
    /grit and fit pour le métier de ([^.]+)/,
    /le métier est ([^.]+)\./
  ];

  for (const pattern of patterns) {
    const match = content.match(pattern);
    if (match && match[1]) {
      return match[1].trim();
    }
  }

  return null;
}

/**
 * Met à jour les variables LUX avec les métiers explorés
 * @param {string} userId - L'ID de l'utilisateur
 * @param {Array} history - L'historique des actions
 * @param {Object} itemsPro - Les interactions pro
 */
export async function updateLuxMetiersVariables(userId, history, itemsPro) {
  if (!userId) return;

  try {
    const luxSessionId = `lux_${userId}`;
    const { metiersExplores, pistesSerieuses } = getMetiersFromHistory(history, itemsPro);

    await vfUpdateVariables(
      luxSessionId,
      {
        metiersExplores,
        pistesSerieuses
      },
      process.env.REACT_APP_VF_LUX_API_KEY
    );

    console.log('Variables LUX métiers mises à jour:', { metiersExplores, pistesSerieuses });
  } catch (error) {
    console.error('Erreur lors de la mise à jour des variables LUX métiers:', error);
  }
}
